import {
    Box,
    CircularProgress,
    DialogContent,
    DialogTitle,
    IconButton,
    Modal,
    ModalDialog,
    Stack,
    Typography,
} from '@mui/joy';
import { Check, Sparkle, X } from '@phosphor-icons/react';
import { useMemo } from 'react';
import { AchievementBadge } from '../AchievementBadge/AchievementBadge';
import { useAchievements } from '../../../hooks/user/useAchievements';

/** Размер значка в карточке. */
const ICON_SIZE = 48;

function ProgressBar({ current, threshold }) {
    const ratio = Math.max(0, Math.min(1, current / threshold));

    return (
        <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 1 }}>
            <Box
                sx={{
                    flex: 1,
                    height: 6,
                    borderRadius: '999px',
                    overflow: 'hidden',
                    backgroundColor: 'neutral.softBg',
                }}
            >
                <Box
                    sx={{
                        width: `${ratio * 100}%`,
                        height: '100%',
                        borderRadius: '999px',
                        backgroundColor: 'text.tertiary',
                    }}
                />
            </Box>
            <Typography level="body-xs" textColor="text.tertiary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                {Math.min(current, threshold)} / {threshold}
            </Typography>
        </Stack>
    );
}

function ShowcaseHint({ max }) {
    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            sx={{ p: 1.5, mb: 1, borderRadius: '24px', backgroundColor: 'background.level1' }}
        >
            <Box
                sx={{
                    width: 40,
                    height: 40,
                    flexShrink: 0,
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    backgroundColor: 'primary.500',
                }}
            >
                <Sparkle size={20} weight="fill" color="#ffffff" />
            </Box>
            <Box>
                <Typography level="title-sm">Достижения в профиле</Typography>
                <Typography level="body-xs" textColor="text.tertiary">
                    Выберите до {max} достижений и они будут кружиться вокруг вашего аватара.
                </Typography>
            </Box>
        </Stack>
    );
}

function AchievementCard({ achievement, selectable, selected, outOfLimit, onToggle }) {
    const locked = !achievement.unlocked;
    // Лимит выбран — остальные карточки не нажимаются, но уже выбранные
    // обязаны оставаться кликабельными, иначе снять выбор было бы нечем.
    const disabled = selectable && !selected && outOfLimit;

    return (
        <Stack
            direction="row"
            alignItems="center"
            spacing={1.5}
            role={selectable ? 'button' : undefined}
            tabIndex={selectable && !disabled ? 0 : -1}
            aria-pressed={selectable ? selected : undefined}
            onClick={() => selectable && !disabled && onToggle()}
            onKeyDown={(e) => {
                if ((e.key === 'Enter' || e.key === ' ') && selectable && !disabled) {
                    e.preventDefault();
                    onToggle();
                }
            }}
            sx={{
                p: 1.5,
                borderRadius: '24px',
                backgroundColor: 'background.level1',
                cursor: selectable && !disabled ? 'pointer' : 'default',
                opacity: disabled ? 0.5 : 1,
                outline: 'none',
                '&:focus-visible': {
                    boxShadow: '0 0 0 2px var(--joy-palette-primary-outlinedBorder)',
                },
            }}
        >
            <AchievementBadge
                code={achievement.code}
                image={achievement.image}
                size={ICON_SIZE}
                locked={locked}
            />

            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography
                    level="title-sm"
                    textColor={locked ? 'text.tertiary' : 'text.primary'}
                    noWrap
                >
                    {achievement.title}
                </Typography>
                <Typography level="body-xs" textColor="text.tertiary">
                    {achievement.description}
                </Typography>

                {achievement.progress && (
                    <ProgressBar
                        current={achievement.progress.current}
                        threshold={achievement.progress.threshold}
                    />
                )}
            </Box>

            {selectable && (
                <Box
                    sx={{
                        width: 24,
                        height: 24,
                        flexShrink: 0,
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        border: '1.5px solid',
                        borderColor: selected ? 'primary.500' : 'neutral.outlinedBorder',
                        backgroundColor: selected ? 'primary.500' : 'transparent',
                    }}
                >
                    {selected && <Check size={16} weight="bold" color="#ffffff" />}
                </Box>
            )}
        </Stack>
    );
}

/**
 * Модалка достижений профиля.
 *
 * В своём профиле в ней же выбирают витрину — достижения, которые будут
 * кружиться вокруг аватара. Новую витрину отдаём наверх (`onShowcaseChange`),
 * чтобы значки перестроились сразу, без перезагрузки профиля.
 *
 * @param {boolean} open
 * @param {Function} setOpen
 * @param {number} userId - чьи достижения показываем
 * @param {Function} [onShowcaseChange] - новая витрина после правки
 */
export default function ModalAchievements({ open = false, setOpen = () => { }, userId, onShowcaseChange }) {
    const {
        achievements, itsme, unlockedCount, total, loading, error,
        isSelected, toggle, showcaseCount, maxShowcase,
    } = useAchievements(userId, open);

    // Список идёт в обратном порядке каталога — новые достижения оказываются
    // сверху, а не в хвосте за полусотней старых.
    const items = useMemo(() => [...achievements].reverse(), [achievements]);

    const outOfLimit = showcaseCount >= maxShowcase;

    const handleToggle = async (achievement) => {
        const next = await toggle(achievement.id);
        if (next) onShowcaseChange?.(next);
    };

    return (
        <Modal open={open} onClose={() => setOpen(false)}>
            <ModalDialog
                layout="center"
                sx={{
                    width: '100%',
                    maxWidth: 500,
                    borderRadius: '32px',
                    border: 'none',
                    p: 3,
                    '@media (max-width: 540px)': {
                        maxWidth: 'calc(100% - 40px)',
                    },
                }}
            >
                <DialogTitle>Достижения</DialogTitle>
                <IconButton
                    aria-label="Закрыть"
                    onClick={() => setOpen(false)}
                    variant="plain"
                    color="neutral"
                    sx={{
                        position: 'absolute',
                        top: '0.75rem',
                        right: '0.75rem',
                        borderRadius: '50px',
                    }}
                >
                    <X size={20} />
                </IconButton>

                <DialogContent sx={{ maxHeight: '60vh', overflowY: 'auto' }}>
                    {loading ? (
                        <Box sx={{ display: 'flex', justifyContent: 'center', py: 3 }}>
                            <CircularProgress />
                        </Box>
                    ) : error ? (
                        <Typography level="body-sm" color="danger">
                            Не удалось загрузить достижения
                        </Typography>
                    ) : items.length === 0 ? (
                        <Typography level="body-sm" textColor="text.tertiary">
                            Достижения не найдены.
                        </Typography>
                    ) : (
                        <Stack spacing={1.25}>
                            {itsme && (
                                <>
                                    <ShowcaseHint max={maxShowcase} />
                                    <Typography level="body-xs" textColor="text.tertiary" textAlign="center">
                                        Получено {unlockedCount} из {total}
                                    </Typography>
                                </>
                            )}

                            {items.map(achievement => (
                                <AchievementCard
                                    key={achievement.id}
                                    achievement={achievement}
                                    selectable={itsme && achievement.unlocked}
                                    selected={isSelected(achievement.id)}
                                    outOfLimit={outOfLimit}
                                    onToggle={() => handleToggle(achievement)}
                                />
                            ))}
                        </Stack>
                    )}
                </DialogContent>
            </ModalDialog>
        </Modal>
    );
}
